import { useCallback } from "react";
import { needsOrgBowlerPick } from "../../../utility/bowlerMatch";

export const useTeamFormValidation = () => {
  // Validation function matching the wizard logic
  const validateForm = useCallback(
    (selectedSquad, selectedEvent, teamName, bowlers) => {
      const errors = {};

      // Validate squad selection
      if (!selectedSquad) {
        errors.squad = "Please select a squad";
      }

      // Validate event selection
      if (!selectedEvent) {
        errors.event = "Please select an event";
      }

      // Validate team name for team events (bowlerQuantity > 2)
      if (selectedEvent?.bowlerQuantity > 2 && !teamName?.trim()) {
        errors.teamName = "Team name is required for team events";
      }

      // Validate bowlers
      if (bowlers && bowlers.length > 0) {
        bowlers.forEach((bowler, index) => {
          if (!bowler?.name?.trim()) {
            errors[`bowler_${index}_name`] = `Bowler ${
              index + 1
            } name is required`;
          } else if (needsOrgBowlerPick(bowler)) {
            errors[`bowler_${index}_name`] = `Pick which ${
              bowler.name
            } this is, or add them as a new bowler`;
          }

          // Only validate average if bowler has average
          if (bowler?.hasAverage !== false) {
            if (!String(bowler?.average || "").trim()) {
              errors[`bowler_${index}_average`] = `Bowler ${
                index + 1
              } average is required`;
            } else {
              const average = parseInt(bowler.average);
              if (isNaN(average) || average < 0 || average > 300) {
                errors[`bowler_${index}_average`] = `Bowler ${
                  index + 1
                } average must be between 0 and 300`;
              }
            }
          }

          // Validate email format if provided
          if (bowler.email && bowler.email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(bowler.email)) {
              errors[`bowler_${index}_email`] = `Bowler ${
                index + 1
              } email format is invalid`;
            }
          }

          if (bowler.phone && bowler.phone.trim()) {
            const phoneRegex = /^[\d\s\-\(\)\+]+$/;
            if (!phoneRegex.test(bowler.phone)) {
              errors[`bowler_${index}_phone`] = `Bowler ${
                index + 1
              } phone format is invalid`;
            }
          }
        });
      }

      return errors;
    },
    []
  );

  // Check if form can be saved
  const canSave = useCallback(
    (selectedEvent, bowlers, teamName) => {
    // Must have selected event and bowlers
    if (!selectedEvent || !bowlers || bowlers.length === 0) {
      return false;
    }

    // Team name is only required for team events (bowlerQuantity > 2)
    const needsTeamName = selectedEvent.bowlerQuantity > 2;
    if (needsTeamName && !teamName?.trim()) {
      return false;
    }

    // All bowlers must have names, and averages only if they have averages
    return bowlers.every((bowler) => {
      const hasName = bowler?.name?.trim();
      
      if (!hasName) {
        return false;
      }
      if (needsOrgBowlerPick(bowler)) {
        return false;
      }

      // Only require average if bowler has average
      if (bowler?.hasAverage !== false) {
        const hasAverage = String(bowler?.average || "").trim();
        
        if (!hasAverage) {
          return false;
        }

        // Validate average is a valid number
        const average = parseInt(bowler.average);
        if (isNaN(average) || average < 0 || average > 300) {
          return false;
        }
      }

      return true;
    });
  },
    []
  );

  return {
    validateForm,
    canSave,
  };
};
